import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RotateCcw, ToggleLeft, ToggleRight } from 'lucide-react';
import type { FilterState } from '@/types/filterTypes';
import { CANDIDATE_STAGES } from '@/types/filterTypes';
import FilterPresets from './FilterPresets';

interface FilterControlsProps {
  filters: FilterState;
  uniqueProfiles: string[];
  positions: { id: string; name: string }[];
  onFilterChange: (key: keyof FilterState, value: string | string[] | { min: number; max: number } | 'include' | 'exclude') => void;
  onResetFilters: () => void;
  hasActiveFilters: boolean;
}

// Display labels for traits (DCTOS framework)
const TRAIT_DISPLAY: { key: keyof FilterState; label: string }[] = [
  { key: 'dominance', label: 'Drive' },
  { key: 'extraversion', label: 'Connection' },
  { key: 'patience', label: 'Task' },
  { key: 'formality', label: 'Order' },
  { key: 'objectivity', label: 'Style' },
];

const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  positions,
  onFilterChange,
  onResetFilters,
  hasActiveFilters
}) => {
  const handleRangeChange = (trait: keyof FilterState, type: 'min' | 'max', value: string) => {
    if (trait === 'position' || trait === 'status' || trait === 'statusMode') return;

    const numValue = parseInt(value) || 0;
    const clampedValue = Math.max(0, Math.min(100, numValue));
    const currentRange = filters[trait] as { min: number; max: number };

    let newRange;
    if (type === 'min') {
      newRange = { min: Math.min(clampedValue, currentRange.max), max: currentRange.max };
    } else {
      newRange = { min: currentRange.min, max: Math.max(clampedValue, currentRange.min) };
    }

    onFilterChange(trait, newRange);
  };

  const handleStageToggle = (stage: string, checked: boolean) => {
    const current = filters.status || [];
    const updated = checked
      ? [...current, stage]
      : current.filter(s => s !== stage);
    onFilterChange('status', updated);
  };

  const handleLoadPreset = (presetFilters: FilterState) => {
    (Object.keys(presetFilters) as (keyof FilterState)[]).forEach(key => {
      onFilterChange(key, presetFilters[key] as any);
    });
  };

  return (
    <div className="space-y-0">
      {/* Saved Presets */}
      <div className="pt-4">
        <FilterPresets currentFilters={filters} onLoadPreset={handleLoadPreset} />
      </div>

      <div className="space-y-6 p-4">
        {/* Header with Reset Button */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-black">Filter Candidates</h3>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onResetFilters}
              className="tesla-button-outline"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          )}
        </div>

        {/* Stage (Status) Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-foreground">Hiring Stage</Label>
            <button
              type="button"
              onClick={() => onFilterChange('statusMode', filters.statusMode === 'include' ? 'exclude' : 'include')}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              title={filters.statusMode === 'include' ? 'Showing only selected stages' : 'Hiding selected stages'}
            >
              {filters.statusMode === 'include' ? (
                <><ToggleRight className="h-3.5 w-3.5 text-primary" /> Show only</>
              ) : (
                <><ToggleLeft className="h-3.5 w-3.5" /> Hide selected</>
              )}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-1.5">
            {CANDIDATE_STAGES.map(stage => (
              <div key={stage} className="flex items-center gap-2">
                <Checkbox
                  id={`stage-${stage}`}
                  checked={filters.status?.includes(stage) ?? false}
                  onCheckedChange={(checked) => handleStageToggle(stage, !!checked)}
                />
                <label
                  htmlFor={`stage-${stage}`}
                  className="text-sm text-foreground cursor-pointer select-none"
                >
                  {stage}
                </label>
              </div>
            ))}
          </div>
          {(filters.status?.length ?? 0) > 0 && (
            <p className="text-xs text-muted-foreground">
              {filters.statusMode === 'include'
                ? `Showing ${filters.status.length} stage${filters.status.length > 1 ? 's' : ''}`
                : `Hiding ${filters.status.length} stage${filters.status.length > 1 ? 's' : ''}`}
            </p>
          )}
        </div>

        {/* Position Filter */}
        <div className="space-y-2">
          <Label htmlFor="position-filter" className="text-sm font-medium text-foreground">
            Position
          </Label>
          <Select value={filters.position} onValueChange={(value) => onFilterChange('position', value)}>
            <SelectTrigger id="position-filter" className="w-full">
              <SelectValue placeholder="All positions" />
            </SelectTrigger>
            <SelectContent className="bg-background border shadow-lg">
              <SelectItem value="all">All Positions</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {positions.map((position) => (
                <SelectItem key={position.id} value={position.id}>
                  {position.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Behavioral Trait Ranges */}
        <div className="space-y-4">
          <Label className="text-sm font-medium text-foreground">
            Behavioral Traits (Ranges)
          </Label>
          <div className="grid grid-cols-1 gap-4">
            {TRAIT_DISPLAY.map(({ key, label }) => {
              const range = filters[key] as { min: number; max: number };
              return (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-medium text-muted-foreground">
                      {label}
                    </Label>
                    <span className="text-xs text-muted-foreground">{range.min}% - {range.max}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={range.min}
                        onChange={(e) => handleRangeChange(key, 'min', e.target.value)}
                        className="text-center text-sm"
                        placeholder="Min"
                      />
                    </div>
                    <span className="text-muted-foreground text-sm">-</span>
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={range.max}
                        onChange={(e) => handleRangeChange(key, 'max', e.target.value)}
                        className="text-center text-sm"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;
